import { Fallback, Image, Root } from '@radix-ui/react-avatar';
import cx from 'clsx';
import { format } from 'date-fns';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import React from 'react';
import { Card, Divider, Stack, TextLink, Type } from 'src/components/core';
// import { DocumentAuthorCard } from 'src/components/document/authorCard.component';
import { DocumentBody } from 'src/components/document/body.component';
import { DocumentTableOfContents } from 'src/components/document/tableOfContents.component';
import { PageLayout } from 'src/components/layout/pageLayout.component';
import { StickyAside } from 'src/components/layout/stickyAside.component';
// import { Avatar } from 'src/components/user/avatar.component';
import { appConfig } from 'src/config';
import type { Document } from 'src/types/documentation';
import { fetchDoc } from 'src/utils/sanity.server';
import { getInitials } from 'src/utils/string';
import { searchForAllCountriesStaticProps } from '../../actions/searchActions';

interface GuideSlugProps {
  guide: Pick<Document, 'slug' | 'content' | 'title' | 'author' | 'date'>;
}

const GuideSlug: NextPage<GuideSlugProps> = ({ guide }) => {
  const router = useRouter();
  if (!router.isFallback && !guide?.slug) {
    return <ErrorPage statusCode={404} />;
  }
  const alt = guide?.author?.name ?? 'avatar';
  return (
    <PageLayout title={guide.title} description={guide.title}>
      <div className="relative grid grid-cols-7 gap-4 pb-4">
        <Card
          component="main"
          className="col-span-7 px-0.5 py-4 sm:col-span-5 sm:px-10 sm:py-6"
        >
          <Stack direction="vertical" gap={['space-y-5', 'sm:space-y-6']}>
            {/* <DocumentHeader document={guide} /> */}
            <div className={cx('')}>
              <Type
                bold
                component="h1"
                variant={['text-3xl', 'sm:text-4xl']}
                className="mb-3.5 text-center sm:mb-5"
              >
                {guide.title}
              </Type>
              <Type
                component="h2"
                colorVariant="faded"
                weight="font-medium"
                variant={['text-lg', 'sm:text-xl']}
                className="mb-2 text-center"
              >
                {appConfig.brand.appName}
              </Type>
              <Stack
                component="h3"
                gap="space-x-2"
                className={'justify-center'}
              >
                {!!guide.author?.picture && (
                  <Root
                    className={cx(
                      'flex items-center justify-center overflow-hidden rounded-full border border-gray-200 border-opacity-20 h-7 w-7'
                    )}
                  >
                    {guide.author.picture && (
                      <Image alt={alt} src={guide.author.picture} />
                    )}
                    <Fallback asChild>
                      <Type variant={'text-sm'}>{getInitials(alt)}</Type>
                    </Fallback>
                  </Root>
                  // <Avatar user={guide.author} size="xsmall" />
                )}
                <Type
                  colorVariant="faded"
                  className={cx('text-center flex items-center space-x-2')}
                  variant={'text-base'}
                >
                  {[
                    guide.author?.name,
                    format(new Date(guide.date), 'LLLL d, yyyy'),
                  ].join(' · ')}
                </Type>
              </Stack>
            </div>
            <Divider />
            <DocumentBody html={guide.content.html} />
          </Stack>
        </Card>
        <StickyAside
          className="col-span-7 sm:col-span-2"
          contentClassName="space-y-4"
        >
          {guide.author && (
            <Card
              className={cx('pt-0 border-t-8')}
              style={{
                borderTopColor: '#27396e',
                // ...style,
              }}
              // {...rest}
            >
              <Stack alignItems="items-start">
                {/* <Avatar className="shrink-0" user={guide?.author} /> */}
                <Root
                  className={cx(
                    'flex items-center justify-center overflow-hidden rounded-full border border-gray-200 border-opacity-20 shrink-0 h-14 w-14'
                  )}
                >
                  {guide.author.picture && (
                    <Image alt={alt} src={guide.author.picture} />
                  )}
                  <Fallback asChild>
                    <Type variant={'text-sm'}>{getInitials(alt)}</Type>
                  </Fallback>
                </Root>
                <div>
                  <Type bold variant="text-lg">
                    {guide?.author?.name}
                  </Type>
                  <Type colorVariant="faded" variant="text-sm">
                    {guide?.author.title}
                  </Type>
                  <TextLink
                    className="text-sm"
                    href={`mailto:${guide?.author.email}`}
                  >
                    {guide?.author?.email}
                  </TextLink>
                </div>
              </Stack>
            </Card>
          )}
          <DocumentTableOfContents headings={guide.content.headings} />
        </StickyAside>
      </div>
    </PageLayout>
  );
};

export default GuideSlug;

export const getStaticProps: GetStaticProps<
  Record<string, unknown>,
  { slug: string }
> = async ({ params, locale }) => {
  const slug = params?.slug ?? '';
  const { doc: guide, revalidate } = await fetchDoc({
    docType: 'guide',
    slug,
  });

  const countries = await searchForAllCountriesStaticProps();

  return {
    props: {
      guide,
      ...(await serverSideTranslations(locale || 'en', [
        'common',
        'about',
        'partners',
        'navbar',
        'new-homepage',
        'new-navbar',
        'new-footer',
      ])),
      countries: countries || [],
    },
    revalidate,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  // const slugs = await fetchDocSlugs({
  //   docType: 'guide',
  // });

  return {
    // paths: slugs.map((slug) => {
    //   return {
    //     params: {
    //       slug,
    //     },
    //   };
    // }),
    // fallback: false,
    paths: [],
    fallback: 'blocking',
  };
};
